export type ColorFamily =
    | 'red'
    | 'orange'
    | 'yellow'
    | 'green'
    | 'cyan'
    | 'blue'
    | 'purple'
    | 'pink'
    | 'gray'
    | 'unknown';

export class ColorThemeUtils {
    /** Devuelve una “familia” de color a partir de HEX (#RGB/#RGBA/#RRGGBB/#RRGGBBAA) */
    static getColorFamily(hex: string): ColorFamily {
        const rgb = this.hexToRgb(hex);
        if (!rgb) return 'unknown';

        const { h, s } = this.rgbToHsl(rgb.r, rgb.g, rgb.b);

        // Poco saturado => gris
        if (s < 0.12) return 'gray';

        // Rangos por tono (hue)
        if (h >= 345 || h < 15) return 'red';
        if (h >= 15 && h < 40) return 'orange';
        if (h >= 40 && h < 65) return 'yellow';
        if (h >= 65 && h < 160) return 'green';
        if (h >= 160 && h < 200) return 'cyan';
        if (h >= 200 && h < 255) return 'blue';
        if (h >= 255 && h < 290) return 'purple';
        if (h >= 290 && h < 330) return 'pink';
        if (h >= 330 && h < 345) return 'red';

        return 'unknown';
    }

    /** Aplica UNA clase al body (y quita las demás del grupo). */
    static applyBodyThemeClass(
        themeClass: string | null | undefined,
        allThemeClasses: readonly string[]
    ): void {
        if (typeof document === 'undefined') return;

        const body = document.body;
        if (!body) return;

        // quita todas las del grupo
        body.classList.remove(...allThemeClasses);

        // añade la que toca
        if (themeClass) body.classList.add(themeClass);
    }

    /** Soporta #RGB, #RGBA, #RRGGBB, #RRGGBBAA */
    static hexToRgb(hex: string): { r: number; g: number; b: number } | null {
        if (!hex) return null;

        let h = hex.trim().replace('#', '');
        if (![3, 4, 6, 8].includes(h.length)) return null;

        // expandir #RGB / #RGBA
        if (h.length === 3 || h.length === 4) {
            h = h
                .split('')
                .map((c) => c + c)
                .join('');
        }

        // ignorar alpha si viene (RRGGBBAA)
        const rr = h.slice(0, 2);
        const gg = h.slice(2, 4);
        const bb = h.slice(4, 6);

        const r = parseInt(rr, 16);
        const g = parseInt(gg, 16);
        const b = parseInt(bb, 16);

        if ([r, g, b].some((n) => Number.isNaN(n))) return null;

        return { r, g, b };
    }

    /** Convierte RGB 0..255 a HSL (h 0..360, s/l 0..1) */
    static rgbToHsl(
        r: number,
        g: number,
        b: number
    ): { h: number; s: number; l: number } {
        r /= 255;
        g /= 255;
        b /= 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const d = max - min;

        let h = 0;
        const l = (max + min) / 2;
        const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));

        if (d !== 0) {
            switch (max) {
                case r:
                    h = ((g - b) / d) % 6;
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            h *= 60;
            if (h < 0) h += 360;
        }

        return { h, s, l };
    }
}
